import React, { useState, useMemo } from 'react';
import { investigationService } from '../services/api';
import { formatDateShort } from '../services/dateUtils';
import '../styles/DefiPanel.css';

/**
 * DefiPanel — Smart Contract / DeFi Interaction Decoding
 *
 * Calls GET /investigations/:id/defi-decode (modules/defi_decoder.py) on demand.
 * For EVM chains (ethereum/polygon/arbitrum) the backend fetches input data from
 * Etherscan for each contract-call transaction and decodes it against a hardcoded
 * 4-byte selector registry (Uniswap V2/V3, ERC-20, Aave V3, Compound, etc — no
 * web3/eth-abi dependency). For non-EVM chains it can only match against the
 * known-contract address registry.
 *
 * This is a "run on demand" panel (not auto-computed like PatternPanel) because
 * it makes one external HTTP call per EVM transaction and can take a while on
 * larger investigations.
 *
 * Props:
 *   investigationId — id of the current investigation
 *   investigation   — investigation object (used for .blockchain)
 */

const EVM_CHAINS = ['ethereum', 'polygon', 'arbitrum'];

const CATEGORY_META = {
  swap:         { icon: '🔄', label: 'Swaps' },
  liquidity:    { icon: '💧', label: 'Liquidity' },
  token:        { icon: '🪙', label: 'Token' },
  lending:      { icon: '🏦', label: 'Lending' },
  stake:        { icon: '🔒', label: 'Staking' },
  wrap:         { icon: '📦', label: 'Wrap/Unwrap' },
  multicall:    { icon: '📋', label: 'Multicall' },
  contract_call:{ icon: '⚙',  label: 'Unidentified call' },
  unknown:      { icon: '⚙',  label: 'Unidentified call' },
};

function addrShort(addr) {
  if (!addr) return '—';
  return `${addr.slice(0, 10)}…${addr.slice(-6)}`;
}

function fmtParamValue(p) {
  if (p.note === 'dynamic') return `(${p.type}, not decoded)`;
  if (p.value === null || p.value === undefined) return '—';
  if (p.type === 'address') return addrShort(p.value);
  if (typeof p.value === 'number' || typeof p.value === 'bigint') return p.value.toLocaleString();
  return String(p.value);
}

export default function DefiPanel({ investigationId, investigation }) {
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [result, setResult]     = useState(null);
  const [expandedSection, setExpandedSection] = useState('interactions');
  const [categoryFilter, setCategoryFilter]   = useState('all');
  const [expandedTx, setExpandedTx]           = useState(null);

  const blockchain = (investigation?.blockchain || '').toLowerCase();
  const isEvm      = EVM_CHAINS.includes(blockchain);

  const handleRun = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await investigationService.decodeDefi(investigationId);
      setResult(res.data);
      setExpandedSection('interactions');
    } catch (err) {
      console.error('DeFi decode failed:', err);
      setError('DeFi decode failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const decoded     = result?.decoded || [];
  const summary     = result?.summary || { total_decoded: 0, by_category: {}, by_protocol: {} };
  const contractMap = result?.contract_map || {};

  const filteredDecoded = useMemo(() => {
    if (categoryFilter === 'all') return decoded;
    return decoded.filter(r => (r.decoded_call?.category || (r.contract_info ? 'token' : 'unknown')) === categoryFilter);
  }, [decoded, categoryFilter]);

  const categoryCounts = summary.by_category || {};
  const protocolCounts = summary.by_protocol || {};
  const contractList   = Object.entries(contractMap);

  const SectionHeader = ({ id, label, badge, badgeCls }) => (
    <button
      className={`dp-section-hdr${expandedSection === id ? ' active' : ''}`}
      onClick={() => setExpandedSection(expandedSection === id ? null : id)}
    >
      <span>{label}</span>
      {badge !== undefined && (
        <span className={`dp-badge ${badgeCls || ''}`}>{badge}</span>
      )}
      <span className="dp-chevron">{expandedSection === id ? '▲' : '▼'}</span>
    </button>
  );

  return (
    <div className="defi-panel">
      <div className="dp-header">
        <h3>🧬 DeFi / Smart Contract Decoder</h3>
        <p className="dp-subtitle">
          Decodes contract-call transactions — DEX swaps, liquidity, lending, staking — that would
          otherwise just show up as a value transfer to a contract address with no context.
        </p>
      </div>

      {!isEvm && (
        <div className="dp-info-box dp-info-warn">
          <strong>{investigation?.blockchain?.toUpperCase() || 'This chain'}</strong> isn't an EVM chain, so
          full input-data decoding isn't available. This run will only match transactions against the
          known-contract registry (exchange/DEX/lending contract addresses), not decode call parameters.
        </div>
      )}

      {!result && !loading && (
        <div className="dp-empty">
          <p>No DeFi analysis has been run yet for this investigation.</p>
          <button className="dp-run-btn" onClick={handleRun}>▶ Run DeFi Decode</button>
          {isEvm && (
            <p className="dp-hint">
              Fetches input data from Etherscan for each contract-call transaction (up to 40), then
              decodes it locally. Requires an ETHERSCAN_API_KEY configured on the backend — if none is
              set, EVM transactions will come back undecoded.
            </p>
          )}
        </div>
      )}

      {loading && (
        <div className="dp-loading">
          <div className="dp-spinner" />
          <p>Fetching &amp; decoding contract calls…</p>
        </div>
      )}

      {error && <div className="dp-info-box dp-info-danger">{error}</div>}

      {result && !loading && (
        <>
          {/* ── Summary strip ── */}
          <div className="dp-summary-strip">
            <div className="dp-stat">
              <div className="dp-stat-value">{summary.total_decoded}</div>
              <div className="dp-stat-label">Interactions</div>
            </div>
            <div className="dp-stat">
              <div className="dp-stat-value">{Object.keys(protocolCounts).length}</div>
              <div className="dp-stat-label">Protocols</div>
            </div>
            <div className="dp-stat">
              <div className="dp-stat-value">{Object.keys(categoryCounts).length}</div>
              <div className="dp-stat-label">Categories</div>
            </div>
            <div className="dp-stat">
              <div className="dp-stat-value">{contractList.length}</div>
              <div className="dp-stat-label">Known Contracts</div>
            </div>
          </div>

          <button className="dp-rerun-btn" onClick={handleRun} disabled={loading}>
            🔁 Re-run
          </button>

          {summary.total_decoded === 0 ? (
            <div className="dp-info-box dp-info-warn" style={{ marginTop: 12 }}>
              No DeFi or contract-call interactions were decoded.
              {isEvm
                ? ' This usually means the transactions in this investigation are plain value transfers between wallets, or ETHERSCAN_API_KEY isn\u2019t configured on the backend.'
                : ' No transactions matched the known-contract registry for this chain.'}
            </div>
          ) : (
            <>
              {/* ── Decoded Interactions ── */}
              <SectionHeader
                id="interactions"
                label="🧬 Decoded Interactions"
                badge={filteredDecoded.length}
              />
              {expandedSection === 'interactions' && (
                <div className="dp-section-body">
                  <div className="dp-filter-row">
                    <button
                      className={`dp-filter-chip${categoryFilter === 'all' ? ' active' : ''}`}
                      onClick={() => setCategoryFilter('all')}
                    >All ({decoded.length})</button>
                    {Object.entries(categoryCounts).map(([cat, count]) => {
                      const meta = CATEGORY_META[cat] || { icon: '⚙', label: cat };
                      return (
                        <button
                          key={cat}
                          className={`dp-filter-chip${categoryFilter === cat ? ' active' : ''}`}
                          onClick={() => setCategoryFilter(cat)}
                        >{meta.icon} {meta.label} ({count})</button>
                      );
                    })}
                  </div>

                  <div className="dp-tx-list">
                    {filteredDecoded.map((r) => {
                      const dc   = r.decoded_call;
                      const cat  = dc?.category || (r.contract_info ? 'token' : 'unknown');
                      const meta = CATEGORY_META[cat] || { icon: '⚙', label: cat };
                      const isOpen = expandedTx === r.tx_hash;
                      const contractName = r.contract_info?.name;
                      return (
                        <div key={r.tx_hash} className="dp-tx-row">
                          <button
                            className="dp-tx-row-main"
                            onClick={() => setExpandedTx(isOpen ? null : r.tx_hash)}
                          >
                            <span className="dp-tx-icon">{dc?.icon || meta.icon}</span>
                            <span className="dp-tx-action">{dc?.action || 'contract interaction'}</span>
                            <span className="dp-tx-protocol">{dc?.protocol || r.contract_info?.protocol || 'unknown'}</span>
                            <code className="dp-tx-hash" title={r.tx_hash}>{addrShort(r.tx_hash)}</code>
                            {r.timestamp && <span className="dp-tx-date">{formatDateShort(r.timestamp)}</span>}
                            <span className="dp-tx-chevron">{isOpen ? '▲' : '▼'}</span>
                          </button>
                          {isOpen && (
                            <div className="dp-tx-detail">
                              <div className="dp-tx-detail-row">
                                <label>From</label>
                                <code title={r.from_address}>{addrShort(r.from_address)}</code>
                              </div>
                              <div className="dp-tx-detail-row">
                                <label>To</label>
                                <code title={r.to_address}>
                                  {contractName ? `${contractName} (${addrShort(r.to_address)})` : addrShort(r.to_address)}
                                </code>
                              </div>
                              {r.value > 0 && (
                                <div className="dp-tx-detail-row">
                                  <label>Value</label>
                                  <span>{r.value} {r.token_symbol || ''}</span>
                                </div>
                              )}
                              {dc?.selector && (
                                <div className="dp-tx-detail-row">
                                  <label>Selector</label>
                                  <code>0x{dc.selector}</code>
                                </div>
                              )}
                              {dc?.params?.length > 0 && (
                                <div className="dp-tx-params">
                                  <label>Decoded parameters <span className="dp-raw-note">(raw on-chain units — not decimal-scaled)</span></label>
                                  {dc.params.map((p, i) => (
                                    <div key={i} className="dp-param-row">
                                      <span className="dp-param-name">{p.name}</span>
                                      <span className="dp-param-type">{p.type}</span>
                                      <span className="dp-param-value">{fmtParamValue(p)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {dc?.action === 'unknown' && (
                                <p className="dp-explainer" style={{ marginTop: 8 }}>
                                  This contract call's 4-byte selector isn't in the known registry, so it
                                  couldn't be identified — it's likely a less-common protocol or a custom contract.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── By Protocol ── */}
              <SectionHeader id="protocols" label="🏷 By Protocol" badge={Object.keys(protocolCounts).length} />
              {expandedSection === 'protocols' && (
                <div className="dp-section-body">
                  <div className="dp-bar-list">
                    {Object.entries(protocolCounts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([protocol, count]) => (
                        <div key={protocol} className="dp-bar-row">
                          <span className="dp-bar-label">{protocol}</span>
                          <div className="dp-bar-track">
                            <div
                              className="dp-bar-fill"
                              style={{ width: `${Math.max(6, (count / summary.total_decoded) * 100)}%` }}
                            />
                          </div>
                          <span className="dp-bar-count">{count}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* ── Known Contracts ── */}
              <SectionHeader id="contracts" label="📜 Known Contracts Seen" badge={contractList.length} />
              {expandedSection === 'contracts' && (
                <div className="dp-section-body">
                  {contractList.length === 0 ? (
                    <p className="dp-none">No transactions touched a recognised contract address.</p>
                  ) : (
                    <div className="dp-addr-list">
                      {contractList.map(([addr, info]) => (
                        <div key={addr} className="dp-addr-row">
                          <code className="dp-addr-code" title={addr}>{addrShort(addr)}</code>
                          <span className="dp-contract-name">{info.name}</span>
                          <span className="dp-contract-protocol">{info.protocol}</span>
                          <button
                            className="dp-copy-btn"
                            onClick={() => navigator.clipboard.writeText(addr)}
                            title="Copy address"
                          >📋</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Interpretation Guide ── */}
              <SectionHeader id="guide" label="📖 Interpretation Guide" />
              {expandedSection === 'guide' && (
                <div className="dp-section-body dp-guide">
                  <div className="dp-guide-item">
                    <strong>🔄 Swap</strong>
                    <p>A DEX trade (Uniswap, etc) exchanging one token for another. Params show the path and amounts in raw on-chain units (not decimal-adjusted for the token's actual decimals).</p>
                  </div>
                  <div className="dp-guide-item">
                    <strong>💧 Liquidity</strong>
                    <p>Depositing into or withdrawing from a liquidity pool — relevant for tracing pooled/co-mingled funds.</p>
                  </div>
                  <div className="dp-guide-item">
                    <strong>🏦 Lending</strong>
                    <p>Supply, withdraw, borrow, or repay on a lending protocol (Aave, Compound). Useful for following collateralised positions.</p>
                  </div>
                  <div className="dp-guide-item">
                    <strong>⚙ Unidentified call</strong>
                    <p>The transaction called a contract, but the 4-byte function selector isn't in the registry. The raw selector and input are still shown for manual lookup (e.g. on a 4-byte signature database).</p>
                  </div>
                  <div className="dp-guide-item">
                    <strong>Decoded parameters</strong>
                    <p>Best-effort decode of fixed-size ABI words (address/uint/bool). Dynamic types (arrays, bytes) are flagged but not decoded — note that amounts are raw integers and have not been scaled by the token's decimals.</p>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
